import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Permission, Role, Session } from '@elutide/shared';
import { api } from '@/lib/api';
import { qk } from '@/lib/keys';

/**
 * The session the shell renders against.
 *
 * A2 owns authentication: the login screen, the lock screen, passkeys, PIN and step-up.
 * A4 owns only this read of `GET /auth/session`, which the shell needs to paint the right
 * nav and the right cards. Role-aware rendering here is a convenience. The API is the
 * control: a screen a role cannot reach still returns 403 if it is typed into the bar, and
 * a field a role may not see never leaves the server.
 */
export interface OpsSessionValue {
  session: Session | null;
  role: Role | null;
  permissions: ReadonlySet<Permission>;
  can: (permission: Permission) => boolean;
  canAny: (...permissions: Permission[]) => boolean;
  isLoading: boolean;
  isError: boolean;
}

const EMPTY: ReadonlySet<Permission> = new Set<Permission>();

const SessionContext = createContext<OpsSessionValue>({
  session: null,
  role: null,
  permissions: EMPTY,
  can: () => false,
  canAny: () => false,
  isLoading: true,
  isError: false,
});

export function SessionProvider({ children }: { children: ReactNode }) {
  const query = useQuery({
    queryKey: qk.session(),
    queryFn: () => api.get<Session>('/auth/session'),
    staleTime: 60_000,
    retry: false,
  });

  const value = useMemo<OpsSessionValue>(() => {
    const session = query.data ?? null;
    const permissions: ReadonlySet<Permission> = session
      ? new Set(session.permissions)
      : EMPTY;
    return {
      session,
      role: session?.role ?? null,
      permissions,
      can: (permission) => permissions.has(permission),
      canAny: (...list) => list.some((permission) => permissions.has(permission)),
      isLoading: query.isLoading,
      isError: query.isError,
    };
  }, [query.data, query.isError, query.isLoading]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): OpsSessionValue {
  return useContext(SessionContext);
}

/** Test and Storybook seam: render a subtree with a fixed session. */
export function SessionFixtureProvider({
  session,
  children,
}: {
  session: Session | null;
  children: ReactNode;
}) {
  const value = useMemo<OpsSessionValue>(() => {
    const permissions: ReadonlySet<Permission> = session ? new Set(session.permissions) : EMPTY;
    return {
      session,
      role: session?.role ?? null,
      permissions,
      can: (permission) => permissions.has(permission),
      canAny: (...list) => list.some((permission) => permissions.has(permission)),
      isLoading: false,
      isError: false,
    };
  }, [session]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
